import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import {
  ExpressionOfInterestRequest,
  ExpressionOfInterestResponse,
  ExpressionOfInterestData,
  CompanyValidationData,
  EoiPaginatedResponse,
  EoiReviewRequest,
  EoiStatus,
} from '../interfaces/expression-of-interest.interface';

@Injectable({ providedIn: 'root' })
export class ExpressionOfInterestService {
  private readonly apiUrl = `${environment.baseUrl}public/expression-of-interest`;
  private readonly adminApiUrl = `${environment.baseUrl}expression-of-interest`;


  constructor(private readonly http: HttpClient) { }

  private hasRole(role: string): boolean {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return Array.isArray(user.roles) && user.roles.includes(role);
  }
  getExpressionOfInterests(
    page: number,
    size: number,
    searchQuery?: string,
    status?: EoiStatus
  ): Observable<EoiPaginatedResponse> {

    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());

    if (searchQuery) {
      params = params.set('searchQuery', searchQuery);
    }

    if (status) {
      params = params.set('status', status);
    }

    const url = this.adminApiUrl;

    return this.http.get<EoiPaginatedResponse>(url, { params }).pipe(
      catchError(this.handleError),
    );
  }

  reviewExpressionOfInterest(id: string, payload: EoiReviewRequest): Observable<any> {
    return this.http.post(`${this.adminApiUrl}/${id}/review`, payload).pipe(
      catchError(this.handleError),
    );
  }

  /**
   * Submit an Expression of Interest
   * @param payload - The EOI request data
   * @returns Observable of the response
   */
  submitExpressionOfInterest(
    payload: ExpressionOfInterestRequest,
  ): Observable<ExpressionOfInterestResponse> {
    return this.http.post<ExpressionOfInterestResponse>(this.apiUrl, payload).pipe(
      map((response) => {
        try {
          return this.validateAndTransformResponse(response);
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to validate expression of interest response';
          throw new HttpErrorResponse({
            error: { message },
            status: 500,
            statusText: 'Response Validation Error',
          });
        }
      }),
      catchError(this.handleError),
    );
  }

  /**
   * Parse company validation response from JSON string
   * @param data - EOI response data
   * @returns Parsed company validation data or null
   */
  parseCompanyValidation(data: ExpressionOfInterestData): CompanyValidationData | null {
    try {
      if (!data.companyValidationResponse) return null;
      return JSON.parse(data.companyValidationResponse) as CompanyValidationData;
    } catch (error) {
      console.warn('Failed to parse company validation response:', error);
      return null;
    }
  }

  /**
   * Get user-friendly status message
   * @param status - The EOI status
   * @returns Human-readable status message
   */
  getStatusMessage(status: string): string {
    const statusMessages: Record<string, string> = {
      PENDING_REVIEW: 'Your Expression of Interest is under review',
      APPROVED: 'Your Expression of Interest has been approved',
      REJECTED: 'Your Expression of Interest requires additional information',
      UNDER_REVIEW: 'Your Expression of Interest is being reviewed',
    };

    return statusMessages[status] || 'Your Expression of Interest has been submitted';
  }

  /**
   * Get user-friendly TIN verification message
   * @param status - The TIN verification status
   * @returns Human-readable verification message
   */
  getTinVerificationMessage(status: string): string {
    const verificationMessages: Record<string, string> = {
      MATCH_REGISTERED: 'Company verified in VFIFC registry',
      NO_MATCH_REGISTERED: 'Company registration required',
      MATCH_EXTERNAL: 'Company verified in external registry',
      VERIFICATION_PENDING: 'TIN verification pending',
    };

    return verificationMessages[status] || 'TIN verification pending';
  }

  /**
   * Validate and transform the API response
   * @param response - Raw API response
   * @returns Validated response
   */
  private validateAndTransformResponse(
    response: ExpressionOfInterestResponse,
  ): ExpressionOfInterestResponse {
    if (!response || typeof response.status !== 'boolean') {
      throw new Error('Invalid response format');
    }

    if (!response.status) {
      throw new Error(response.message || 'Request failed');
    }

    if (!response.data || !response.data.id) {
      throw new Error('Invalid response data');
    }

    return response;
  }

  /**
   * Handle HTTP errors
   * @param error - HTTP error response
   * @returns Error observable
   */
  private handleError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = 'An unexpected error occurred. Please try again.';

    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = `Network error: ${error.error.message}`;
    } else {
      // Server-side error
      if (error.error?.message) {
        errorMessage = error.error.message;
      } else if (error.status === 0) {
        errorMessage = 'Unable to connect to the server. Please check your internet connection.';
      } else if (error.status >= 400 && error.status < 500) {
        errorMessage = 'Invalid request. Please check your information and try again.';
      } else if (error.status >= 500) {
        errorMessage = 'Server error. Please try again later.';
      }
    }

    console.error('Expression of Interest Service Error:', error);
    return throwError(() => new Error(errorMessage));
  }

  getExpressionOfInterest(id: string): Observable<ExpressionOfInterestResponse> {
    return this.http.get<ExpressionOfInterestResponse>(`${this.adminApiUrl}/${id}`).pipe(
      catchError(this.handleError),
    );
  }
}
